// HelpPage.jsx — 使用手册 / 帮助页（/help）。
//
// 需求方原话："考虑在系统页面上的较显眼位置提供统一的，双语的，使用手册/帮助文档/求助
// 按钮。减少不断向成员普及系统用法的摩擦。"
//
// 为什么是前端路由而不是 data_content 里的一篇文档：那棵树按岗位的 view glob 授权、走发布
// 管线。手册讲的是系统本身，必须对**每一个**登录账号可达——恰恰是看不到那些 glob 的人最
// 需要读它（"为什么那些节点是灰的"）。放进产品文档树就会同时继承授权闸与发布耦合。
//
// 本文件只负责**排版**：所有文字、章节顺序、数字都在 helpContent.mjs 里，由 helpContent
// .test.mjs 与后端 defaults.h / ticket_types.cpp 逐条对账（SB-042 约定：可判定逻辑入 .mjs）。
// 这里刻意不写任何一句业务事实——手册受 DP-6 约束，写在 JSX 里的句子没有闸看着。
//
// 窄屏（DP-11）：全篇单列、无表格、无横向容器；类型/状态清单用"徽章 + 文字"的堆叠行而不是
// <Table>，因为四列表格在 390px 下必然把横滚条推到 body 上。
import { useEffect } from 'react';
import { Alert, Box, Button, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useLang, useT } from '../i18n/index.mjs';
import TicketStatusChip from '../components/TicketStatusChip.jsx';
import {
  HELP_ASK,
  HELP_ASSIGNED_FLOW,
  HELP_INCIDENT_FLOW,
  HELP_INTRO,
  HELP_SECTIONS,
  HELP_STATUS_FLOW,
  HELP_TICKET_TYPES,
  HELP_TITLE,
  attachLimitsLine,
  inlineSegments,
  pickText,
  sectionToc,
} from './helpContent.mjs';

// 行内加粗。手册里 **…** 标的是"读漏就要付代价"的那几句（撤回不可逆、建单后仍可补传）；
// 切分逻辑在 helpContent.mjs 里配了单测，这里只把片段摆出来。
function Rich({ text }) {
  return (
    <>
      {inlineSegments(text).map((seg, i) => (
        <Box key={i} component="span" sx={{ fontWeight: seg.bold ? 700 : 'inherit' }}>
          {seg.text}
        </Box>
      ))}
    </>
  );
}

// 类型清单：编号前缀徽章 + 既有的 ticketType.* 标签 + 一句"什么时候用它"。
// 标签走既有键而不是手册自带译名——用户在下拉框里看到的必须与手册是同一个词。
function TicketTypeList({ lang, t }) {
  return (
    <Stack spacing={1.5} sx={{ mt: 1 }}>
      {HELP_TICKET_TYPES.map((row) => (
        <Box key={row.type}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}>
            <Chip size="small" variant="outlined" label={`${row.prefix}-`} sx={{ fontFamily: 'monospace' }} />
            <Typography sx={{ fontWeight: 700 }} variant="subtitle2">
              {t(`ticketType.${row.type}`)}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
            <Rich text={pickText(row, lang)} />
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

// 状态清单：直接复用成员在"我的提交"里看到的那个彩色徽章组件。手册里的状态名与
// 页面上的状态名若各自翻译，读者会以为那是两回事。
function StatusList({ rows, lang }) {
  return (
    <Stack spacing={1.5} sx={{ mt: 1 }}>
      {rows.map((row) => (
        <Box key={row.status}>
          <TicketStatusChip status={row.status} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
            <Rich text={pickText(row, lang)} />
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

function Block({ block, lang, t }) {
  const text = pickText(block, lang);
  switch (block.k) {
    case 'p':
      return (
        <Typography variant="body1" sx={{ mt: 1.5, wordBreak: 'break-word' }}>
          <Rich text={text} />
        </Typography>
      );
    case 'ul':
    case 'ol':
      return (
        <Box
          component={block.k === 'ol' ? 'ol' : 'ul'}
          sx={{ mt: 1.5, mb: 0, pl: 3, '& li': { mb: 1 } }}
        >
          {block.items.map((item, i) => (
            <Typography key={i} component="li" variant="body2" sx={{ wordBreak: 'break-word' }}>
              <Rich text={pickText(item, lang)} />
            </Typography>
          ))}
        </Box>
      );
    case 'note':
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            <Rich text={text} />
          </Typography>
        </Alert>
      );
    case 'warn':
      // 醒目提示留给"不可逆 / 已经被误解过并付了代价"的那几条，不滥用——
      // 满屏黄色等于没有重点。
      return (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            <Rich text={text} />
          </Typography>
        </Alert>
      );
    case 'link':
      return (
        <Button
          component={RouterLink}
          to={block.to}
          variant="outlined"
          size="small"
          sx={{ mt: 2 }}
        >
          {text}
        </Button>
      );
    case 'ticketTypes':
      return <TicketTypeList lang={lang} t={t} />;
    case 'statusFlow':
      return <StatusList rows={HELP_STATUS_FLOW} lang={lang} />;
    case 'incidentFlow':
      return <StatusList rows={HELP_INCIDENT_FLOW} lang={lang} />;
    case 'assignedFlow':
      // SB-190 步 5：指派单那条线，与事故线同一个状态清单组件（同一枚彩色徽章）。
      return <StatusList rows={HELP_ASSIGNED_FLOW} lang={lang} />;
    case 'attachLimits':
      // 附件上限**不在**手册里另存一份文字：这句话由 attachments.mjs 的 ATTACH_KINDS 表
      // 现场拼出来，与提交表单下方那行同源（D3：手写的第二份话术漂移过半年）。
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {attachLimitsLine(t)}
          </Typography>
        </Alert>
      );
    default:
      // 不认识的块类型宁可不画，也不要抛异常把整页手册变成白屏——
      // helpContent.test.mjs 已经把"块类型必须在清单里"钉死在构建期。
      return null;
  }
}

export default function HelpPage() {
  const t = useT();
  const { lang } = useLang();
  const toc = sectionToc(lang);
  const { hash } = useLocation();

  // SB-151：带 #id 抵达时滚到对应章节（各页页头的 ❓ 走 `/help#<sectionId>` 进来）。
  // 姿势照 DocBrowserPage 的锚点 effect：SPA 内导航不触发浏览器原生的锚点跳转
  // （页面没有重新加载，只有 location 变了），所以这里必须自己滚一次。手册内容是
  // 同步渲染的静态数据，effect 执行时章节必已在 DOM 里；id 解不出就静默不动，
  // 绝不跳错。scrollMarginTop 已在章节 Paper 上（sticky AppBar 不盖标题）。
  useEffect(() => {
    if (!hash) return;
    let id = hash.slice(1);
    try { id = decodeURIComponent(id); } catch { /* 保留原样 */ }
    if (!id) return;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ block: 'start' });
  }, [hash]);

  return (
    // 单列、有可读宽度上限。AppShell 的 Container 是 maxWidth={false}（宽屏不留大边距），
    // 所以正文自己收口——一行 160 字符的手册没人读得下去。
    <Box sx={{ maxWidth: 880, mx: 'auto' }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
        {pickText(HELP_TITLE, lang)}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mt: 1, wordBreak: 'break-word' }}>
        <Rich text={pickText(HELP_INTRO, lang)} />
      </Typography>

      {/* 求助入口置顶。手册最该服务的是**还不知道该问什么**的人：先给他一个不必读完
          全文就能按的按钮，比把求助方式埋在第七节有用得多。 */}
      <Paper
        variant="outlined"
        sx={{ mt: 3, p: { xs: 2, sm: 2.5 }, borderColor: 'primary.main', borderWidth: 2 }}
      >
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
          {pickText(HELP_ASK.title, lang)}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, wordBreak: 'break-word' }}>
          <Rich text={pickText(HELP_ASK.lead, lang)} />
        </Typography>
        <Button
          component={RouterLink}
          to={HELP_ASK.to}
          variant="contained"
          sx={{ mt: 2 }}
        >
          {pickText(HELP_ASK.cta, lang)}
        </Button>
        <Box component="ul" sx={{ mt: 2, mb: 0, pl: 3, '& li': { mb: 0.75 } }}>
          {HELP_ASK.notes.map((n, i) => (
            <Typography
              key={i}
              component="li"
              variant="body2"
              color="text.secondary"
              sx={{ wordBreak: 'break-word' }}
            >
              <Rich text={pickText(n, lang)} />
            </Typography>
          ))}
        </Box>
      </Paper>

      {/* ── 第一百零九批 D：命令行工具与密件的入口 ─────────────────────────
          /cli **刻意不进主导航**（横向预算第一百零二/一百零四批就用满了，而它是一个
          一次性入口），所以它必须在手册里有一条真链接——否则那一页无从抵达，而它是
          「用 key 才能取到 key」那条死锁的一半。判据在 appNav.test.mjs：/cli 不在
          NAV_ENTRIES 里，且本页（或 helpContent）里必须找得到它。
          文案走 i18n 键而不是这里的字面句子——本文件只排版（见头注）。 */}
      <Paper variant="outlined" sx={{ mt: 3, p: { xs: 2, sm: 2.5 } }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
          {t('cli.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary"
                    sx={{ mt: 1, wordBreak: 'break-word' }}>
          {t('cli.subtitle')}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
          <Button component={RouterLink} to="/cli" variant="outlined">
            {t('cli.download')}
          </Button>
          <Button component={RouterLink} to="/secrets">
            {t('shell.nav.secrets')}
          </Button>
        </Stack>
      </Paper>

      {/* 目录：页内锚点。窄屏上会换行成多排（flexWrap），不产生横向滚动。
          注意 href="#id" 是**原生锚点**：浏览器自理滚动、不走 router，所以点击后
          useLocation().hash 保持陈旧——将来若要按 hash 高亮"当前章"，先把这里换成
          router 导航，否则读到的是旧值（SB-151 证伪评审留档）。 */}
      <Stack direction="row" spacing={1} sx={{ mt: 3, flexWrap: 'wrap', rowGap: 1 }}>
        {toc.map((s) => (
          <Chip
            key={s.id}
            component="a"
            href={`#${s.id}`}
            clickable
            size="small"
            variant="outlined"
            label={s.title}
          />
        ))}
      </Stack>

      {HELP_SECTIONS.map((section) => (
        <Paper
          key={section.id}
          id={section.id}
          variant="outlined"
          sx={{
            mt: 3,
            p: { xs: 2, sm: 3 },
            // AppBar 是 position:sticky，锚点跳转不留出这段就会被工具条盖住标题——
            // 读者点了目录却看不到自己点的那一节，只会以为链接坏了。
            scrollMarginTop: 80,
          }}
        >
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
            {pickText(section.title, lang)}
          </Typography>
          <Divider sx={{ mt: 1 }} />
          {section.blocks.map((block, i) => (
            <Block key={i} block={block} lang={lang} t={t} />
          ))}
        </Paper>
      ))}
    </Box>
  );
}
